/**
 * wells_on_location — downhole, Directional & MWD. GET
 * /dashboard/wells-on-location (api#1141): wells mobilizing, on location or
 * released, each with the crew on it.
 *
 * WITH THE MODE OFF THE ROUTE SENDS `dd_mode: false`, NO WELLS AND NO COUNTS.
 * That is "this company does not run wells here", and it says so; it is never
 * drawn as "no wells on location", which would read as a quiet day.
 *
 * Day on location is the API's `days_on_location` (null → —). Crew roles use
 * the downhole labels; an unknown role or a missing name is —.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { useWellsOnLocation } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { DH_JOB_STATUS_SKIN, DH_PERSONNEL_ROLE_LABEL } from '../../../lib/downhole-labels';
import type { WellOnLocation, WellsOnLocation } from '../../../types/overview-operations';

export function crewLine(w: Pick<WellOnLocation, 'crew_on'>): string {
  if (w.crew_on.length === 0) return 'No crew on';
  return w.crew_on.map((c) => `${dash(c.name)} (${DH_PERSONNEL_ROLE_LABEL[c.personnel_role] ?? '—'})`).join(', ');
}

function Body({ d }: { d: WellsOnLocation }) {
  const navigate = useNavigate();
  if (!d.dd_mode) {
    return (
      <p data-testid="wol-mode-off" className="text-sm text-fieldiq-muted">
        Directional &amp; MWD is off, so wells and crew are not tracked here. An admin can turn it on in Settings › Operations › Downhole.
      </p>
    );
  }
  return (
    <>
      {d.counts && (
        <div className="grid grid-cols-3 gap-4">
          <WidgetFigure testId="wol-mobilizing" label="Mobilizing" value={d.counts.mobilizing} />
          <WidgetFigure testId="wol-on-location" label="On location" value={d.counts.on_location} />
          <WidgetFigure testId="wol-released" label="Released" value={d.counts.released} />
        </div>
      )}
      {d.wells.length === 0 ? (
        <p data-testid="wol-empty" className="mt-4 text-sm text-fieldiq-muted">No wells mobilizing, on location or released.</p>
      ) : (
        <div data-testid="wol-wells" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.wells.map((w) => {
            const skin = DH_JOB_STATUS_SKIN[w.status];
            return (
              <OpsRow key={w.id} to={w.link} onOpen={navigate} testId={`wol-well-${w.id}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-fieldiq-text">{dash(w.job_number)} · {dash(w.well_name)}</span>
                  <span className="block truncate text-fieldiq-muted">{dash(w.rig_name)} · {dash(w.customer_name)}</span>
                  <span data-testid="wol-crew" className="block truncate text-fieldiq-muted">{crewLine(w)}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <OpsBadge fill={skin?.fill ?? 'bg-fi-signal-slate'}>{skin?.label ?? '—'}</OpsBadge>
                  <span className="tabular-nums text-fieldiq-text">Day {dash(w.days_on_location)}</span>
                </span>
              </OpsRow>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function WellsOnLocationWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('downhole_tools');
  const q = useWellsOnLocation(on);
  return (
    <WidgetPanel widgetKey="wells_on_location" eyebrow="Directional & MWD" title="Wells & Crew on Location" link={on ? { label: 'Wells', to: '/downhole/wells' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="wol-vertical-off">Downhole is not on for this company.</VerticalOff>
        ) : (
        <OpsWidgetRead q={q} subject="wells on location" allowed="admins, office staff and dispatchers">
          {(d) => <Body d={d} />}
        </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
